import React, { lazy, Suspense, useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import './App.css';

import GNB1 from './components/gnb1.js';
import GNB2 from './components/gnb2.js';
import SettingsNav from './components/settings-nav.js';
import { SettingsProvider } from './contexts/SettingsContext';
import MCC from './mcc.js';
import WidgetContainer from './components/widget/widget-container.js';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import TestErrorComponent from './components/TestErrorComponent/TestErrorComponent';
import SessionExpirationAlert from './components/SessionExpirationAlert';
import PopupLogin from './components/popup/popup-login.js';
import { LanguageProvider } from './i18n/LanguageContext';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { LocalPostProvider } from './contexts/LocalPostContext';
import { MyPostStateProvider } from './contexts/MyPostStateContext';
import { CreditProvider } from './contexts/CreditContext';
import { useSessionExpiration } from './hooks/useSessionExpiration.js';
import { useWebSocketInit } from './hooks/useWebSocketInit.js';
import ToastContainer from './components/common/ToastContainer';
import RouteLoadingFallback from './components/common/RouteLoadingFallback';
import { ToolTabProvider } from './contexts/ToolTabContext';
import { BottomPanelProvider } from './contexts/BottomPanelContext';
import { PostActionBarProvider } from './contexts/PostActionBarContext';
import { PostDraftProvider } from './contexts/PostDraftContext';
import ToolTabContainer from './components/tool-tab/ToolTabContainer';
import BottomPanel from './components/tool-tab/BottomPanel';
import OAuthLoginOverlay from './components/OAuthLoginOverlay';
import { useScrollbarAutoHide } from './hooks/useScrollbarAutoHide';

const MOBILE_BREAKPOINT = 768;
const SubprojectRoot = lazy(() => import('./subproject/SubprojectRoot'));

// 프로바이더에서 돌아온 최초 소셜 로그인 콜백(전체 페이지 리다이렉트) 여부.
// 계정 연결(oauth_link_mode) 흐름은 기존 화면을 유지해야 하므로 제외한다.
const isFreshOAuthLoginCallback = () => {
  try {
    if (window.location.pathname !== '/oauth/callback') return false;
    if (!window.location.search) return false;
    if (sessionStorage.getItem('oauth_link_mode') === 'true') return false;
    return true;
  } catch {
    return false;
  }
};

// /oauth/callback을 벗어난 뒤 다음 페이지가 실제로 페인트될 때까지
// (더블 rAF) 기다렸다가 오버레이 제거를 알려준다.
function OAuthLoginTransitionWatcher({ onDone }) {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/oauth/callback') return undefined;

    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(onDone);
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [location.pathname, onDone]);

  return null;
}

const FrameLayout = () => {
  const location = useLocation();
  const isLandingRoute = location.pathname.startsWith('/intro');
  const isSynapseRoute = location.pathname.startsWith('/synapse');
  const isFullScreenRoute = isSynapseRoute || isLandingRoute;
  const isSettingsRoute = location.pathname === '/settings';
  const STORAGE_KEY_SIDEBAR_COLLAPSED = 'astrantis_sidebar_collapsed';
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [popupMode, setPopupMode] = useState('login');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_SIDEBAR_COLLAPSED);
      return saved === 'true';
    } catch (error) {
      console.warn('Failed to load sidebar state:', error);
      return false;
    }
  });
  
  const toggleSidebar = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const updateFrameVars = useCallback(() => {
    const w = window.innerWidth;
    let leftW, rightW;
    if (w <= 768) {
      leftW = 0; rightW = 0;
    } else if (w <= 1280) {
      leftW = 80; rightW = 0;
    } else if (w <= 1400) {
      leftW = isCollapsed ? 80 : 300; rightW = 0;
    } else {
      leftW = isCollapsed ? 80 : 300; rightW = 300;
    }
    document.documentElement.style.setProperty('--frame-left-w', `${leftW}px`);
    document.documentElement.style.setProperty('--frame-right-w', `${rightW}px`);
  }, [isCollapsed]);

  /* MCC(#main-contents-container) 실제 bounds → 하단 패널 폭·위치 동기화 */
  const updateMccVars = useCallback(() => {
    const mcc = document.getElementById('main-contents-container');
    if (!mcc) return;
    const { left, width } = mcc.getBoundingClientRect();
    document.documentElement.style.setProperty('--mcc-left', `${left}px`);
    document.documentElement.style.setProperty('--mcc-width', `${width}px`);
  }, []);

  const syncLayoutVars = useCallback(() => {
    updateFrameVars();
    requestAnimationFrame(updateMccVars);
  }, [updateFrameVars, updateMccVars]);

  useEffect(() => {
    syncLayoutVars();
  }, [syncLayoutVars]);

  useEffect(() => {
    const mcc = document.getElementById('main-contents-container');
    if (!mcc) return undefined;

    const ro = new ResizeObserver(() => updateMccVars());
    ro.observe(mcc);

    const mccParent = mcc.parentElement;
    if (mccParent) ro.observe(mccParent);

    return () => ro.disconnect();
  }, [updateMccVars, location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
      syncLayoutVars();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [syncLayoutVars]);

  useEffect(() => {
    if (mobileMenuOpen) setMobileMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close menu on route change only
  }, [location.pathname]);
  
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SIDEBAR_COLLAPSED, isCollapsed.toString());
    } catch (error) {
      console.warn('Failed to save sidebar state:', error);
    }
  }, [isCollapsed]);

  // 로그인·회원가입 팝업 열기 함수를 전역으로 제공
  useEffect(() => {
    window.openLoginPopup = () => { setPopupMode('login'); setShowLoginPopup(true); };
    window.openSignupPopup = () => { setPopupMode('signup'); setShowLoginPopup(true); };
    window.closeLoginPopup = () => setShowLoginPopup(false);
    return () => {
      delete window.openLoginPopup;
      delete window.openSignupPopup;
      delete window.closeLoginPopup;
    };
  }, []);

  const frameClassName = `frame-changable${isFullScreenRoute ? ' canvas-full' : ''}${isLandingRoute ? ' landing-full' : ''}${isCollapsed && !isSynapseRoute ? ' sidebar-collapsed' : ''}`;

  const handleLoginSuccess = () => {
    setShowLoginPopup(false);
    // 페이지 새로고침은 PopupLogin 컴포넌트에서 처리
  };

  const frameContent = (
    <>
    <div className={frameClassName}>
      <div className="frame-changable-child">
        {!isLandingRoute && (
          <>
            <GNB1
              isMobile={isMobile}
              isMobileMenuOpen={mobileMenuOpen}
              onToggleMobileMenu={isMobile ? () => setMobileMenuOpen((open) => !open) : undefined}
            />
            {!isMobile && !isSynapseRoute && (
              isSettingsRoute
                ? <SettingsNav />
                : <GNB2 isCollapsed={isCollapsed} onToggleSidebar={toggleSidebar} />
            )}
          </>
        )}
      </div>
      <div className="frame-changable-child">
        <div id="main-contents-container">
          <MCC/>
          {/* <APITest />
          <WebSocketTest /> */}
        </div>
      </div>
      <div className="frame-changable-child" id="sub-side-bar">
        {!isLandingRoute && <WidgetContainer />}
      </div>
      <PopupLogin
        display={showLoginPopup ? 'flex' : 'none'}
        onClose={() => setShowLoginPopup(false)}
        onLoginSuccess={handleLoginSuccess}
        initialMode={popupMode}
      />
    </div>
    {isMobile && mobileMenuOpen && (
      <div
        className="gnb2-mobile-overlay"
        onClick={() => setMobileMenuOpen(false)}
        role="button"
        tabIndex={0}
        aria-label="메뉴 닫기"
        onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
      >
        <div className="gnb2-mobile-drawer" onClick={(e) => e.stopPropagation()}>
          {isSettingsRoute
            ? <SettingsNav isMobileOverlay />
            : !isSynapseRoute && <GNB2
                isCollapsed={false}
                onToggleSidebar={() => setMobileMenuOpen(false)}
                isMobileOverlay
                onCloseOverlay={() => setMobileMenuOpen(false)}
              />
          }
        </div>
      </div>
    )}
    </>
  );

  return isSettingsRoute
    ? <SettingsProvider>{frameContent}</SettingsProvider>
    : frameContent;
};

// AppContent 컴포넌트 (SessionProvider 내부에서 사용)
function AppContent() {
  const { 
    isVerifying, 
    isAuthenticated, 
    verifyAuth, 
    setExpiresIn,
    expiresIn 
  } = useSession();

  // 테마 초기화
  useEffect(() => {
    const STORAGE_KEY_THEME = 'astrantis_theme';
    try {
      const savedTheme = localStorage.getItem(STORAGE_KEY_THEME);
      const theme = savedTheme || 'scientific-clam'; // 기본값: scientific-clam
      document.documentElement.setAttribute('data-theme', theme);
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
      document.documentElement.setAttribute('data-theme', 'scientific-clam');
    }
  }, []);

  // 폰트 초기화
  useEffect(() => {
    const STORAGE_KEY_FONT = 'astrantis_font';
    try {
      const savedFont = localStorage.getItem(STORAGE_KEY_FONT);
      if (savedFont) document.documentElement.setAttribute('data-font', savedFont);
    } catch (error) {
      console.warn('Failed to load font from localStorage:', error);
    }
  }, []);

  // 커스텀 hooks 사용: 세션 만료 관리
  const {
    showExpirationAlert,
    remainingSeconds,
    handleExtendSession,
    handleLogout
  } = useSessionExpiration({
    verifyAuth,
    expiresIn,
    setExpiresIn,
    isVerifying
  });

  // 커스텀 hook 사용: WebSocket 초기화
  useWebSocketInit({ isVerifying, isAuthenticated });

  // ✅ 초기 로딩 체크 (ref 사용)
  const hasLoadedRef = useRef(false);
  const [showInitialLoading, setShowInitialLoading] = useState(true);

  // 최초 소셜 로그인 콜백 전체 화면 오버레이: /oauth/callback 로 갓 리다이렉트되어 온
  // 경우에만 켜지고, 콜백 처리가 끝나 다음 페이지가 실제로 그려진 뒤 꺼진다.
  const [oauthOverlayActive, setOauthOverlayActive] = useState(isFreshOAuthLoginCallback);
  const handleOAuthOverlayDone = useCallback(() => setOauthOverlayActive(false), []);

  useEffect(() => {
    // 최초 검증 완료 시 로딩 화면 숨김
    if (!isVerifying && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      setShowInitialLoading(false);
    }
  }, [isVerifying]);

  // ✅ 초기 로딩만 전체 화면으로 표시 (컴포넌트 트리 유지)
  if (showInitialLoading) {
    if (oauthOverlayActive) {
      return <OAuthLoginOverlay />;
    }
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <PostActionBarProvider>
    <PostDraftProvider>
    <ToolTabProvider>
      <BottomPanelProvider>
        <LanguageProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            {oauthOverlayActive && (
              <OAuthLoginTransitionWatcher onDone={handleOAuthOverlayDone} />
            )}
            <Routes>
              <Route path="/test-error" element={<TestErrorComponent />} />

              {/* 메인 레이아웃(FrameLayout)을 거치지 않는 독립 부가 기능 */}
              <Route path="/subproject/*" element={
                <Suspense fallback={<RouteLoadingFallback fullViewport />}>
                  <SubprojectRoot />
                </Suspense>
              } />

              {/* 구 URL 호환: /canvas -> /subproject/canvas */}
              <Route path="/canvas" element={<Navigate to="/subproject/canvas" replace />} />

              <Route path="*" element={
                <>
                  <FrameLayout />
                  {showExpirationAlert && remainingSeconds !== null && (
                    <SessionExpirationAlert
                      onExtend={handleExtendSession}
                      onLogout={handleLogout}
                      remainingSeconds={remainingSeconds}
                    />
                  )}
                  <ToastContainer />
                  <ToolTabContainer />
                  <BottomPanel />
                </>
              }/>
            </Routes>
          </Router>
          {oauthOverlayActive && <OAuthLoginOverlay />}
        </LanguageProvider>
      </BottomPanelProvider>
    </ToolTabProvider>
    </PostDraftProvider>
    </PostActionBarProvider>
  );
}

// 메인 App 컴포넌트
function App() {
  useScrollbarAutoHide();

  return (
    <ErrorBoundary>
      <SessionProvider>
        <LocalPostProvider>
          <MyPostStateProvider>
            <CreditProvider>
              <AppContent />
            </CreditProvider>
          </MyPostStateProvider>
        </LocalPostProvider>
      </SessionProvider>
    </ErrorBoundary>
  );
}

export default App;
